import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import PageCard from './components/PageCard';
import { formatCurrencyView } from './utils/currencyUtils';
import usePdfReports from './components/hooks/usePdfReports';
import usePdfModal from './components/hooks/usePdfModal';
import DefaultModal from './components/modals/DefaultModal';
import LoadingModal from './components/modals/LoadingModal';

const MESES_CURTOS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function ConciliacaoBancaria() {
  const anoBaseStorage = localStorage.getItem('anoBase');
  const anoBase = parseInt(anoBaseStorage || '', 10);
  const hasAnoBase = !Number.isNaN(anoBase);
  const anoAtual = new Date().getFullYear();
  const mesAtual = String(new Date().getMonth() + 1).padStart(2, '0');
  // Ano atual: botões até o mês corrente. Demais anos: janeiro a dezembro.
  const maxMes = anoBase === anoAtual ? new Date().getMonth() + 1 : 12;
  const mesAnoInicial = hasAnoBase
    ? `${anoBase}-${anoBase === anoAtual ? mesAtual : '12'}`
    : new Date().toISOString().slice(0, 7);

  const [contas, setContas] = useState([]);
  const [contaSelecionada, setContaSelecionada] = useState('');
  const [mesAno, setMesAno] = useState(mesAnoInicial); // YYYY-MM
  const [conciliacao, setConciliacao] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');

  // Função para abrir PDF no modal PdfViewer
  const openPdfInViewer = (pdfUrl, title = 'Relatório') => {
    localStorage.setItem('pdfViewerData', JSON.stringify({ url: pdfUrl, title }));
    const event = new CustomEvent('pdfViewerUpdate', { detail: { url: pdfUrl, title } });
    window.dispatchEvent(event);
  };

  // Hooks para PDF e Modal
  const {
    modalData,
    showDefaultModal,
    showModalWithPdfAction,
    closeModal: closeDefaultModal,
  } = usePdfModal();

  const { generatePdfReport } = usePdfReports({
    showSuccessModal: showModalWithPdfAction,
    showErrorModal: showDefaultModal,
    setLoading,
    openPdfViewer: openPdfInViewer,
  });

  useEffect(() => {
    carregarContas();
  }, []);

  useEffect(() => {
    if (!contaSelecionada || !mesAno) return;
    carregarDadosConciliacao();
  }, [contaSelecionada, mesAno]);

  const carregarContas = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL.CONTA}/all`, {
        params: { cnpjAssociacao: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      });
      const contasData = Array.isArray(response.data) ? response.data : [];
      setContas(contasData);
      if (contasData.length === 1) {
        setContaSelecionada(contasData[0].numConta);
      }
    } catch (error) {
      console.error('Erro ao carregar contas:', error);
      setContas([]);
    }
  };

  const carregarDadosConciliacao = async () => {
    if (!contaSelecionada || !mesAno) {
      alert('Selecione uma conta e o mês/ano para conciliação');
      return;
    }

    setLoading(true);
    try {
      const [ano, mes] = mesAno.split('-').map(Number);

      const response = await axios.get(`${API_BASE_URL.CONCILIACAO_BANCARIA}`, {
        params: {
          cnpjAssociacao: cnpj,
          numConta: contaSelecionada,
          ano,
          mes
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      setConciliacao(response.data);
    } catch (error) {
      console.error('Erro ao carregar dados de conciliação:', error);
      alert('Erro ao carregar dados. Verifique o console para mais detalhes.');
      setConciliacao(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMesAnoChange = (value) => {
    if (hasAnoBase) {
      setMesAno(`${anoBase}-${value}`);
    } else {
      setMesAno(value);
    }
  };

  // Função para gerar o PDF da conciliação bancária
  const handleGerarPdf = async () => {
    if (!contaSelecionada || !mesAno) {
      showDefaultModal('error', 'Selecione uma conta e o mês/ano para gerar o relatório');
      return;
    }

    if (!conciliacao) {
      showDefaultModal('error', 'É necessário carregar os dados da conciliação primeiro');
      return;
    }

    const [ano, mes] = mesAno.split('-').map(Number);

    await generatePdfReport(
      'conciliacao_bancaria',
      {
        cnpjAssociacao: cnpj,
        numConta: contaSelecionada,
        ano,
        mes
      },
      'Conferência Financeira',
      'Falha ao gerar o relatório de conferência financeira.',
      { autoOpen: true }
    );
  };


  return (
    <PageCard title="Conferência Financeira" icon="bi-bank">
              {/* Filtros */}
              <div className="row g-2 mb-3">
                <div className="col-md-4 d-flex flex-column justify-content-between">
                  <label className="form-label fw-bold">Conta Bancária</label>
                  <select
                    className="form-select form-select-sm"
                    value={contaSelecionada}
                    onChange={e => setContaSelecionada(e.target.value)}
                  >
                    <option value="">Selecione uma conta...</option>
                    {contas.map(conta => (
                      <option key={conta.numConta} value={conta.numConta}>
                        {conta.numConta} - {conta.nomeBanco}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 d-flex flex-column justify-content-between">
                  <label className="form-label fw-bold">
                    {hasAnoBase ? `Mês (Ano base: ${anoBase})` : 'Mês/Ano'}
                  </label>
                  {hasAnoBase ? (
                    <div className="d-flex flex-nowrap gap-1 align-items-center" style={{ overflowX: 'auto' }}>
                      {MESES_CURTOS.map((nomeMes, idx) => {
                        const mes = idx + 1;
                        const selecionado = Number(mesAno.split('-')[1] || 0) === mes;
                        return (
                          <button
                            key={mes}
                            type="button"
                            className={`btn btn-sm flex-fill ${selecionado ? 'btn-success' : 'btn-outline-success'}`}
                            style={{ minWidth: 44 }}
                            disabled={mes > maxMes}
                            onClick={() => handleMesAnoChange(String(mes).padStart(2, '0'))}
                          >
                            {nomeMes}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <input
                      type="month"
                      className="form-control"
                      value={mesAno}
                      onChange={e => handleMesAnoChange(e.target.value)}
                    />
                  )}
                </div>
                {/* Botão para gerar PDF */}
                <div className="col-md-2 d-flex flex-column justify-content-end">
                  <button
                    className="btn btn-danger btn-sm"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={handleGerarPdf}
                    disabled={!conciliacao || isLoading}
                  >
                    <i className="bi bi-file-earmark-pdf me-2"></i>
                    Gerar PDF
                  </button>
                </div>
              </div>
              {/* Botão de conciliação removido conforme solicitado */}

              {/* Resumo da Conciliação */}
              {!isLoading && conciliacao && (
                <>
                  <div className="row g-2 mb-2">
                    <div className="col-12">
                      <div className="card border-success">
                        <div className="card-body py-2 px-3">
                          <h6 className="card-title mb-2" style={{color: 'rgb(7, 59, 43)'}}>
                            <i className="bi bi-calculator me-2"></i>
                            Resumo
                          </h6>
                          <table className="table table-sm mb-0">
                            <tbody>
                              <tr>
                                <td className="fw-bold text-success">
                                  <i className="bi bi-plus-circle me-1"></i>
                                  Rendimentos do Mês
                                </td>
                                <td className="text-end text-success">{formatCurrencyView(conciliacao.rendimentoMes)}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold text-success">
                                  <i className="bi bi-plus-circle me-1"></i>
                                  Rendimentos acumulados até o mês (ano base)
                                </td>
                                <td className="text-end text-success">
                                  {formatCurrencyView(conciliacao.rendimentoAcumulado)}
                                </td>
                              </tr>
                              <tr>
                                <td className="fw-bold text-info">
                                  <i className="bi bi-plus-circle me-1"></i>
                                  Receitas do Mês
                                </td>
                                <td className="text-end text-info">{formatCurrencyView(conciliacao.receitaMes)}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold text-primary">
                                  <i className="bi bi-graph-up-arrow me-1"></i>
                                  Receitas acumuladas até o mês (ano base)
                                </td>
                                <td className="text-end text-primary">
                                  {formatCurrencyView(conciliacao.receitaAcumulada)}
                                </td>
                              </tr>
                              <tr>
                                <td className="fw-bold text-warning">
                                  <i className="bi bi-arrow-left-right me-1"></i>
                                  Despesa paga até mês anterior
                                </td>
                                <td className="text-end text-warning">
                                  {formatCurrencyView(conciliacao.despesaMesAnterior)}
                                </td>
                              </tr>
                              <tr>
                                <td className="fw-bold text-danger">
                                  <i className="bi bi-dash-circle me-1"></i>
                                  Despesas Pagas no Mês
                                </td>
                                <td className="text-end text-danger">{formatCurrencyView(conciliacao.despesaMes)}</td>
                              </tr>
                              <tr className="table-light">
                                <td className="fw-bold fs-6">
                                  <i className="bi bi-laptop me-1"></i>
                                  Saldo do Sistema
                                </td>
                                <td className="text-end fw-bold fs-6">{formatCurrencyView(conciliacao.saldoCalculado)}</td>
                              </tr>
                              <tr className="table-primary">
                                <td className="fw-bold fs-6">
                                  <i className="bi bi-bank me-1"></i>
                                  Saldo Extrato Bancário
                                </td>
                                <td className="text-end fw-bold fs-6">{formatCurrencyView(conciliacao.saldoBancario)}</td>
                              </tr>
                              <tr className={conciliacao.diferenca === 0 ? 'table-success' : 'table-warning'}>
                                <td className="fw-bold fs-5">
                                  <i className={`bi bi-${conciliacao.diferenca === 0 ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
                                  Diferença:
                                  {conciliacao.diferenca !== 0 && (
                                    <span className="ms-1 text-warning-emphasis">
                                      {conciliacao.diferenca > 0
                                        ? 'O saldo do extrato bancário está maior que o saldo do sistema'
                                        : 'O saldo do sistema está maior que o saldo do extrato bancário'}
                                    </span>
                                  )}
                                </td>
                                <td className="text-end fw-bold fs-5">
                                  {formatCurrencyView(Math.abs(conciliacao.diferenca))}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          
                          {conciliacao.diferenca === 0 && (
                            <div className="alert alert-success mt-3 mb-0">
                              <i className="bi bi-check-circle-fill me-2"></i>
                              <strong>Conciliado!</strong> O saldo do extrato bancário está de acordo com o saldo do sistema.
                            </div>
                          )}
                          {conciliacao.diferenca !== 0 && (
                            <div className="alert alert-warning mt-3 mb-0">
                              <i className="bi bi-exclamation-triangle-fill me-2"></i>
                              <strong>Atenção!</strong> Há diferença entre o saldo do extrato bancário e o saldo do sistema.
                              Verifique lançamentos pendentes, cheques não compensados ou erros de registro.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Detalhamento */}
                  <div className="accordion" id="accordionConciliacao">
                    {/* Receitas */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseReceitas"
                        >
                          <i className="bi bi-graph-up me-2 text-info"></i>
                          Receitas ({conciliacao.receitas?.length || 0})
                        </button>
                      </h2>
                      <div id="collapseReceitas" className="accordion-collapse collapse" data-bs-parent="#accordionConciliacao">
                        <div className="accordion-body">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Data</th>
                                  <th>Descrição</th>
                                  <th>Fonte</th>
                                  <th className="text-end">Valor</th>
                                </tr>
                              </thead>
                              <tbody>
                                {conciliacao.receitas?.map((r, idx) => (
                                  <tr key={idx}>
                                    <td>{new Date(r.data + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                                    <td>{r.descricao}</td>
                                    <td>{r.idFonteRecurso}</td>
                                    <td className="text-end text-info fw-bold">
                                      {formatCurrencyView(r.valor)}
                                    </td>
                                  </tr>
                                ))}
                                {(!conciliacao.receitas || conciliacao.receitas.length === 0) && (
                                  <tr>
                                    <td colSpan="4" className="text-center text-muted">
                                      Nenhuma receita neste período
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                              {conciliacao.receitas && conciliacao.receitas.length > 0 && (
                                <tfoot className="table-light fw-bold">
                                  <tr>
                                    <td colSpan="3">Total</td>
                                    <td className="text-end text-info">
                                      {formatCurrencyView(conciliacao.receitaMes)}
                                    </td>
                                  </tr>
                                </tfoot>
                              )}
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Despesas */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseDespesas"
                        >
                          <i className="bi bi-graph-down me-2 text-danger"></i>
                          Despesas Pagas ({conciliacao.despesas?.length || 0})
                        </button>
                      </h2>
                      <div id="collapseDespesas" className="accordion-collapse collapse" data-bs-parent="#accordionConciliacao">
                        <div className="accordion-body">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Data Pgto</th>
                                  <th>Descrição</th>
                                  <th>Favorecido</th>
                                  <th>Forma Pgto</th>
                                  <th className="text-end">Valor</th>
                                </tr>
                              </thead>
                              <tbody>
                                {conciliacao.despesas?.map((d, idx) => (
                                  <tr key={idx}>
                                    <td>{new Date(d.dataPagamento + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                                    <td>{d.objetoResumido || d.objetoDetalhado}</td>
                                    <td>{d.nomeFavorecido || d.idFavorecido}</td>
                                    <td>{d.formaPagamento}</td>
                                    <td className="text-end text-danger fw-bold">
                                      {formatCurrencyView(d.valor)}
                                    </td>
                                  </tr>
                                ))}
                                {(!conciliacao.despesas || conciliacao.despesas.length === 0) && (
                                  <tr>
                                    <td colSpan="5" className="text-center text-muted">
                                      Nenhuma despesa paga neste período
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                              {conciliacao.despesas && conciliacao.despesas.length > 0 && (
                                <tfoot className="table-light fw-bold">
                                  <tr>
                                    <td colSpan="4">Total</td>
                                    <td className="text-end text-danger">
                                      {formatCurrencyView(conciliacao.despesaMes)}
                                    </td>
                                  </tr>
                                </tfoot>
                              )}
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {!contaSelecionada && !isLoading && (
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  Selecione uma conta bancária, o mês/ano e informe o saldo do extrato bancário para realizar a conciliação.
                </div>
              )}
              
              {/* Modais obrigatórios */}
              <LoadingModal loading={isLoading} />
              <DefaultModal
                show={modalData.show}
                onClose={closeDefaultModal}
                title={modalData.title}
                message={modalData.message}
                type={modalData.type}
                onConfirm={modalData.onConfirm}
                pdfData={modalData.pdfData}
              />
    </PageCard>
  );
}

export default ConciliacaoBancaria;
